import moment from "moment";
import * as XLSX from "xlsx";

import {
  BULK_RECORD_STATUS,
  isDataDiscrepancyRecord,
  isFailedRecord,
  isSuccessRecord,
} from "@/utils/greenCoffeeBulkImport";

// Separate storage key so green-coffee and parchment-deliveries don't collide.
export const BULK_RESULT_STORAGE_KEY = "parchmentDeliveryBulkImportResult";

export {
  BULK_RECORD_STATUS,
  isDataDiscrepancyRecord,
  isFailedRecord,
  isSuccessRecord,
};

/**
 * Warning / issue types shown in the parchment deliveries bulk result.
 * (Dummy data: these are synthesized so every warning type can be demonstrated.)
 */
export const BULK_ISSUE_TYPE = {
  OUTTURN_NUMBER_MISMATCH: "OUTTURN_NUMBER_MISMATCH",
  DUPLICATE_OUTTURN_NUMBER: "DUPLICATE_OUTTURN_NUMBER",
  MISSING_OUTTURN_NUMBER: "MISSING_OUTTURN_NUMBER",
  GROWER_IDENTITY_MISMATCH: "GROWER_IDENTITY_MISMATCH",
  MISSING_GROWER_CREDENTIALS: "MISSING_GROWER_CREDENTIALS",
  WEIGHT_MISMATCH: "WEIGHT_MISMATCH",
  MISSING_WEIGHT_VALUE: "MISSING_WEIGHT_VALUE",
  DELIVERY_DATE_MISMATCH: "DELIVERY_DATE_MISMATCH",
  BAGS_QUANTITY_MISMATCH: "BAGS_QUANTITY_MISMATCH",
};

export const BULK_ISSUE_I18N_KEYS = {
  [BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH]: {
    title: "issueOutturnNumberMismatchTitle",
    description: "issueOutturnNumberMismatchDescription",
    filterLabel: "filterLabelOutturnMismatch",
  },
  [BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER]: {
    title: "issueDuplicateOutturnNumberTitle",
    description: "issueDuplicateOutturnNumberDescription",
    filterLabel: "filterLabelDuplicateOutturn",
  },
  [BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER]: {
    title: "issueMissingOutturnNumberTitle",
    description: "issueMissingOutturnNumberDescription",
    filterLabel: "filterLabelMissingOutturn",
  },
  [BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH]: {
    title: "issueGrowerIdentityMismatchTitle",
    description: "issueGrowerIdentityMismatchDescription",
    filterLabel: "filterLabelGrowerMismatch",
  },
  [BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS]: {
    title: "issueMissingGrowerCredentialsTitle",
    description: "issueMissingGrowerCredentialsDescription",
    filterLabel: "filterLabelMissingGrower",
  },
  [BULK_ISSUE_TYPE.WEIGHT_MISMATCH]: {
    title: "issueWeightMismatchTitle",
    description: "issueWeightMismatchDescription",
    filterLabel: "filterLabelWeightMismatch",
  },
  [BULK_ISSUE_TYPE.MISSING_WEIGHT_VALUE]: {
    title: "issueMissingWeightValueTitle",
    description: "issueMissingWeightValueDescription",
    filterLabel: "filterLabelMissingWeight",
  },
  [BULK_ISSUE_TYPE.DELIVERY_DATE_MISMATCH]: {
    title: "issueDeliveryDateMismatchTitle",
    description: "issueDeliveryDateMismatchDescription",
    filterLabel: "filterLabelDeliveryDate",
  },
  [BULK_ISSUE_TYPE.BAGS_QUANTITY_MISMATCH]: {
    title: "issueBagsQuantityMismatchTitle",
    description: "issueBagsQuantityMismatchDescription",
    filterLabel: "filterLabelBagsMismatch",
  },
};

const OUTTURN_RELATED_ISSUE_TYPES = new Set([
  BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH,
  BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER,
  BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER,
]);

const GROWER_RELATED_ISSUE_TYPES = new Set([
  BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH,
  BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS,
]);

const LOT_OUTTURN_COLUMN_ISSUE_TYPES = new Set([
  ...OUTTURN_RELATED_ISSUE_TYPES,
  BULK_ISSUE_TYPE.DELIVERY_DATE_MISMATCH,
]);

const WEIGHTS_COLUMN_ISSUE_TYPES = new Set([
  BULK_ISSUE_TYPE.WEIGHT_MISMATCH,
  BULK_ISSUE_TYPE.MISSING_WEIGHT_VALUE,
  BULK_ISSUE_TYPE.BAGS_QUANTITY_MISMATCH,
]);

/** Payload field keys highlighted / resolved per issue type in the detail dialog */
export const ISSUE_AFFECTED_FIELD_KEYS = {
  [BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH]: ["outturnNumber"],
  [BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER]: ["outturnNumber"],
  [BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER]: ["outturnNumber"],
  [BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH]: ["growerName", "growerCode"],
  [BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS]: ["growerName", "growerCode"],
  [BULK_ISSUE_TYPE.WEIGHT_MISMATCH]: ["grossWeight", "netWeight"],
  [BULK_ISSUE_TYPE.MISSING_WEIGHT_VALUE]: ["netWeight", "netWeightWithBags"],
  [BULK_ISSUE_TYPE.DELIVERY_DATE_MISMATCH]: ["deliveryDate"],
  [BULK_ISSUE_TYPE.BAGS_QUANTITY_MISMATCH]: ["bags"],
};

/** Issues where the user picks factory (saved) vs uploaded values */
export const RESOLVABLE_ISSUE_TYPES = new Set([
  BULK_ISSUE_TYPE.BAGS_QUANTITY_MISMATCH,
  BULK_ISSUE_TYPE.DELIVERY_DATE_MISMATCH,
  BULK_ISSUE_TYPE.WEIGHT_MISMATCH,
  BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH,
  BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS,
  BULK_ISSUE_TYPE.MISSING_WEIGHT_VALUE,
]);

/**
 * Hard-block issue types — outturn not found in DB or duplicate/missing.
 * These records cannot be matched to an existing DB record.
 */
export const HARD_BLOCK_ISSUE_TYPES = new Set([
  BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH,
  BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER,
  BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER,
]);

/** Record whose outturn was not found (or is missing/duplicate) in DB. */
export function isHardBlockRecord(record) {
  return isDataDiscrepancyRecord(record) && HARD_BLOCK_ISSUE_TYPES.has(record?.issueType);
}

/**
 * Record eligible for the "Override factory data" action:
 * - Outturn number found in DB (not a hard-block)
 * - Delivery date matches the DB record
 * Soft attribute issues (grower, weights, etc.) are warnings only, not blockers.
 */
export function isOverrideEligibleRecord(record) {
  if (!isDataDiscrepancyRecord(record)) return false;
  if (isHardBlockRecord(record)) return false;
  return Boolean(record.deliveryDateMatchesDb);
}

export function isOutturnRelatedIssue(issueType) {
  return OUTTURN_RELATED_ISSUE_TYPES.has(issueType);
}

export function isGrowerRelatedIssue(issueType) {
  return GROWER_RELATED_ISSUE_TYPES.has(issueType);
}

export function isLotOutturnColumnIssue(issueType) {
  return LOT_OUTTURN_COLUMN_ISSUE_TYPES.has(issueType);
}

export function isWeightsColumnIssue(issueType) {
  return WEIGHTS_COLUMN_ISSUE_TYPES.has(issueType);
}

export function getRecordIssueType(record) {
  if (!isDataDiscrepancyRecord(record)) return null;
  return resolveBulkIssueType(record);
}

export function resolveBulkIssueType(record) {
  if (record?.issueType && BULK_ISSUE_I18N_KEYS[record.issueType]) {
    return record.issueType;
  }
  // For safety fallback: show an outturn-related warning.
  return BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH;
}

const DISPLAY_DATE_MONTHS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

const PARCHMENT_DATE_PARSE_FORMATS = [
  moment.ISO_8601,
  "YYYY-MM-DD",
  "DD/MM/YYYY",
  "D/M/YYYY",
  "DD-MMM-YYYY",
  "DD-MMM-YY",
];

/**
 * Canonical storage for delivery dates (YYYY-MM-DD). Handles Excel Date objects and serials.
 */
export function normalizeParchmentDeliveryDateInput(value) {
  if (value == null || value === "") return "";

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    return moment(value).format("YYYY-MM-DD");
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const dc = XLSX.SSF.parse_date_code(value);
    if (dc && dc.y) {
      const pad = (n) => String(n).padStart(2, "0");
      return `${dc.y}-${pad(dc.m)}-${pad(dc.d)}`;
    }
  }

  const s = String(value).trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);

  const strict = moment(s, PARCHMENT_DATE_PARSE_FORMATS, true);
  if (strict.isValid()) return strict.format("YYYY-MM-DD");

  const loose = moment(s);
  return loose.isValid() ? loose.format("YYYY-MM-DD") : "";
}

/** Display format aligned with legacy DateMixin (DD-MMM-YYYY) and design (uppercase month). */
export function formatParchmentDisplayDate(value) {
  const iso = normalizeParchmentDeliveryDateInput(value);
  if (!iso) return "—";

  const parts = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!parts) return "—";

  const month = DISPLAY_DATE_MONTHS[Number(parts[2]) - 1] || parts[2];
  return `${parts[3]}-${month}-${parts[1]}`;
}

/** Bags and kg remainder from total quantity and unit size (kg per bag). */
export function computeParchmentBagsFromQuantity(totalQuantity, unitSize) {
  const total = Number(totalQuantity) || 0;
  const unit = Number(unitSize) || 0;
  if (!unit) {
    return { totalBags: 0, leftoverKg: total };
  }
  return {
    totalBags: Math.floor(total / unit),
    leftoverKg: Number((total % unit).toFixed(2)),
  };
}

export const MOCK_PARCHMENT_PROCESSING_BATCHES = [
  {
    id: "batch-1",
    batchId: "PB-986-1",
    lotDate: "20-MAR-2026",
    lotId: "Alex Jones",
    endDate: "PO-1002",
    variety: "Arabica, Typica",
    coffeeType: "P1",
    typeInfo: "Arabica, Typica",
    processInfo: "Process: Natural (Dry)",
    quantity: 1200,
  },
  {
    id: "batch-2",
    batchId: "PB-986-2",
    lotDate: "21-MAR-2026",
    lotId: "KIBOCHI STAR",
    endDate: "PO-1004",
    variety: "Arabica, SL28",
    coffeeType: "P1",
    typeInfo: "Arabica, SL28",
    processInfo: "Process: Washed",
    quantity: 800,
  },
  {
    id: "batch-3",
    batchId: "PB-986-3",
    lotDate: "22-MAR-2026",
    lotId: "PILIPILI FOXES",
    endDate: "PO-1005",
    variety: "Arabica, Bourbon",
    coffeeType: "P1",
    typeInfo: "Arabica, Bourbon",
    processInfo: "Process: Natural (Dry)",
    quantity: 1000,
  },
  {
    id: "batch-4",
    batchId: "PB-986-4",
    lotDate: "23-MAR-2026",
    lotId: "STAR MILL",
    endDate: "PO-1006",
    variety: "Robusta",
    coffeeType: "PL",
    typeInfo: "Robusta",
    processInfo: "Process: Washed",
    quantity: 1000,
  },
];

/** Map a list-table row into the shape expected by ParchmentDeliveryEditDialog. */
export function mapListRowToParchmentEditRecord(item) {
  if (!item) return null;

  if (item.payloadJsonData || item.payload) {
    return {
      id: item.id,
      payloadJsonData: item.payloadJsonData || item.payload,
    };
  }

  const processingBatchIds = Array.isArray(item.processingBatchIds)
    ? [...item.processingBatchIds]
    : [];
  const lot = item.processingBatchLot;
  if (lot && !processingBatchIds.length) {
    const match = MOCK_PARCHMENT_PROCESSING_BATCHES.find((b) => b.batchId === lot);
    if (match) {
      processingBatchIds.push(match.id);
    }
  }

  return {
    id: item.id,
    parchmentDeliveryId: item.parchmentDeliveryId,
    status: item.status,
    payloadJsonData: {
      id: item.id ?? item.parchmentDeliveryId,
      deliveryDate: item.deliveryDate,
      outturnNumber: item.outturnNumber,
      coffeeType: item.coffeeType,
      netWeight: item.netWeight,
      grossWeight: item.grossWeight,
      netWeightWithBags: item.netWeightWithBags ?? item.netWeight,
      processingBatchIds,
      season: item.season,
      growerCode: item.growerCode,
      growerName: item.growerName,
      grower: item.grower ?? item.growerCode,
      unitSize: item.unitSize,
      moisture: item.moisture,
      bags: item.bags,
      leftoverHandling: item.leftoverHandling,
    },
  };
}

function formatParchmentWeight(value) {
  const s = String(value ?? "").trim();
  return s ? `${s} kg` : "—";
}

function getUploadedPayload(record) {
  const uploaded = { ...(record?.payloadJsonData || record?.payload || {}) };
  if (uploaded.deliveryDate != null && uploaded.deliveryDate !== "") {
    uploaded.deliveryDate = normalizeParchmentDeliveryDateInput(uploaded.deliveryDate);
  }
  return uploaded;
}

function getFactoryPayload(record) {
  const uploaded = getUploadedPayload(record);
  const stored = record?.recordedJsonData;
  let factory;
  if (stored && Object.keys(stored).length) {
    factory = { ...uploaded, ...stored };
  } else {
    const issueType = getRecordIssueType(record);
    factory = issueType
      ? buildDummyFactorySnapshot(uploaded, issueType, record?.issueContext || {})
      : { ...uploaded };
  }
  if (factory.deliveryDate != null && factory.deliveryDate !== "") {
    factory.deliveryDate = normalizeParchmentDeliveryDateInput(factory.deliveryDate);
  }
  return factory;
}

function buildDummyFactorySnapshot(uploaded, issueType, issueContext) {
  const factory = { ...uploaded };
  const ctx = issueContext || {};

  switch (issueType) {
    case BULK_ISSUE_TYPE.BAGS_QUANTITY_MISMATCH: {
      const uploadedBags = Number(uploaded.bags) || 33;
      factory.bags = String(uploadedBags === 48 ? "33" : "48");
      break;
    }
    case BULK_ISSUE_TYPE.DELIVERY_DATE_MISMATCH:
      factory.deliveryDate = ctx.factoryDeliveryDate || "2024-01-04";
      break;
    case BULK_ISSUE_TYPE.WEIGHT_MISMATCH:
      factory.grossWeight = ctx.factoryGrossWeight || "8200";
      factory.netWeight = ctx.factoryNetWeight || "2280";
      factory.netWeightWithBags = factory.netWeight;
      break;
    case BULK_ISSUE_TYPE.MISSING_WEIGHT_VALUE:
      factory.netWeight = ctx.factoryNetWeight || "2380";
      factory.netWeightWithBags = factory.netWeight;
      factory.grossWeight = ctx.factoryGrossWeight || "8000";
      break;
    case BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH:
      factory.growerName = ctx.mobileGrowerName || "James Ochieng";
      factory.grower = ctx.factoryGrowerCode || "CG0041";
      break;
    case BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS:
      factory.growerName = ctx.factoryGrowerName || "KIBOCHI STAR";
      factory.grower = ctx.factoryGrowerCode || "CG0037";
      break;
    case BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH:
      factory.outturnNumber = ctx.factoryOutturnNumber || "86KK00158";
      break;
    default:
      break;
  }
  return factory;
}

export function formatParchmentFieldDisplay(fieldKey, payload) {
  const p = payload || {};
  switch (fieldKey) {
    case "outturnNumber":
      return String(p.outturnNumber || "").trim() || "—";
    case "deliveryDate":
      return formatParchmentDisplayDate(p.deliveryDate);
    case "growerName":
      return String(p.growerName || "").trim() || "—";
    case "growerCode":
      return String(p.grower || "").trim() || "—";
    case "bags": {
      const b = String(p.bags || "").trim();
      return b ? `${b} Bags` : "—";
    }
    case "grossWeight":
      return formatParchmentWeight(p.grossWeight);
    case "netWeight":
      return formatParchmentWeight(p.netWeight || p.netWeightWithBags);
    case "netWeightWithBags":
      return formatParchmentWeight(p.netWeightWithBags);
    default:
      return String(p[fieldKey] ?? "").trim() || "—";
  }
}

export function getFieldIssueMap(record) {
  const issueType = getRecordIssueType(record);
  if (!issueType) return {};
  const keys = ISSUE_AFFECTED_FIELD_KEYS[issueType] || [];
  const map = {};
  keys.forEach((fieldKey) => {
    map[fieldKey] = { issueType, i18n: BULK_ISSUE_I18N_KEYS[issueType] };
  });
  return map;
}

export function getResolvableFieldConflicts(record) {
  const issueType = getRecordIssueType(record);
  if (!issueType || !RESOLVABLE_ISSUE_TYPES.has(issueType)) return [];

  const uploaded = getUploadedPayload(record);
  const factory = getFactoryPayload(record);
  const fieldKeys = ISSUE_AFFECTED_FIELD_KEYS[issueType] || [];

  return fieldKeys.map((fieldKey) => ({
    fieldKey,
    issueType,
    factoryValue: formatParchmentFieldDisplay(fieldKey, factory),
    uploadedValue: formatParchmentFieldDisplay(fieldKey, uploaded),
    factoryRaw: factory,
    uploadedRaw: uploaded,
  }));
}

export function mergeResolutionIntoPayload(record, selections) {
  const uploaded = { ...getUploadedPayload(record) };
  const conflicts = getResolvableFieldConflicts(record);

  conflicts.forEach((conflict) => {
    const source = selections[conflict.fieldKey] === "uploaded" ? conflict.uploadedRaw : conflict.factoryRaw;
    if (conflict.fieldKey === "growerCode") {
      uploaded.grower = source.grower != null ? source.grower : source.growerCode;
    } else if (source[conflict.fieldKey] != null) {
      uploaded[conflict.fieldKey] = source[conflict.fieldKey];
    } else if (conflict.fieldKey === "netWeight" && source.netWeightWithBags != null) {
      uploaded.netWeight = source.netWeightWithBags;
    }
  });

  if (uploaded.deliveryDate != null && uploaded.deliveryDate !== "") {
    uploaded.deliveryDate = normalizeParchmentDeliveryDateInput(uploaded.deliveryDate);
  }

  return uploaded;
}

/**
 * Maps a bulk-import result row into the parchment delivery detail view model (dummy batch/QR data until API exists).
 */
export function buildParchmentDeliveryDetailView(record, payloadOverride) {
  const p = payloadOverride || getUploadedPayload(record);
  const lotId =
    String(p.id || record?.parchmentDeliveryId || "").trim() || "—";
  const outturn = String(p.outturnNumber || "").trim() || "—";
  const deliveryDate = formatParchmentDisplayDate(p.deliveryDate);
  const netWeightRaw = String(p.netWeight || p.netWeightWithBags || "").trim();
  const batchLot =
    String(record?.processingBatchLot || p.processingBatchLot || "").trim();

  let statusLabel = "Failed";
  let statusType = "failed";
  let listStatusKey = null;
  if (isSuccessRecord(record)) {
    statusLabel = "Success";
    statusType = "success";
  } else if (isDataDiscrepancyRecord(record)) {
    statusLabel = "Issues Detected";
    statusType = "issues";
  } else if (!isFailedRecord(record)) {
    const listStatus = String(record?.status || "").toLowerCase();
    if (listStatus === "delivered") {
      statusLabel = "Delivered";
      statusType = "success";
      listStatusKey = "delivered";
    } else if (listStatus === "draft") {
      statusLabel = "Draft";
      statusType = "draft";
      listStatusKey = "draft";
    }
  }

  const issueType = getRecordIssueType(record);
  const isOutturnHardBlock =
    statusType === "issues" &&
    (issueType === BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH ||
      issueType === BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER);
  const resolutionConflicts = getResolvableFieldConflicts(record);
  const fieldIssues = getFieldIssueMap(record);

  return {
    issueType,
    resolutionConflicts,
    fieldIssues,
    hasResolvableDiscrepancy:
      statusType === "issues" && resolutionConflicts.length > 0 && !record?.resolutionApplied,
    lotId,
    outturnNumber: outturn,
    deliveryDate,
    season: String(p.season || "").trim() || "2025 - 2026",
    growerName: String(p.growerName || "").trim() || "—",
    growerCode: String(p.grower || "").trim() || "—",
    coffeeType: String(p.coffeeType || "").trim() || "—",
    bags: String(p.bags || "").trim() || "—",
    grossWeight: formatParchmentWeight(p.grossWeight),
    netWeightWithBags: formatParchmentWeight(p.netWeightWithBags),
    netWeight: formatParchmentWeight(netWeightRaw),
    broker: String(p.broker || "").trim() || "—",
    statusLabel,
    statusType,
    listStatusKey,
    processingBatchLot: {
      batchId: isOutturnHardBlock ? "—" : batchLot || "PB-1002",
      speciesVariety: isOutturnHardBlock ? "—" : "Arabica, Typica",
      processLine: isOutturnHardBlock ? "—" : "Process: Natural (Dry)",
      quantity: isOutturnHardBlock
        ? "—"
        : netWeightRaw
          ? `${netWeightRaw} kg`
          : "2400 kg",
    },
    processingBatchDetails: {
      startDate: isOutturnHardBlock
        ? "—"
        : deliveryDate !== "—"
          ? deliveryDate
          : "05-JAN-2026",
      endDate: isOutturnHardBlock
        ? "—"
        : deliveryDate !== "—"
          ? deliveryDate
          : "05-JAN-2026",
      totalCoffeeCherry: isOutturnHardBlock ? "—" : "500 kg",
      airHumidity: isOutturnHardBlock ? "—" : "25%",
      density: isOutturnHardBlock ? "—" : "5 kg",
      temperature: isOutturnHardBlock ? "—" : "4 C",
      beanMoistureContent: isOutturnHardBlock
        ? "—"
        : p.moisture
          ? `${String(p.moisture).trim()}%`
          : "40%",
      processType: isOutturnHardBlock ? "—" : "Natural (Dry)",
      batchRating: isOutturnHardBlock ? "—" : "PB",
      parchmentProduced: isOutturnHardBlock ? "—" : "500 kg",
      statusLabel: isOutturnHardBlock ? "—" : "Completed",
    },
    qrCodes: {
      internalId: isOutturnHardBlock ? "—" : "PB-0001",
      externalId: isOutturnHardBlock ? "—" : "EID-0994",
      internalValue: isOutturnHardBlock ? "" : `PD:${lotId}`,
      externalValue: isOutturnHardBlock ? "" : `EID:${outturn}`,
    },
  };
}

// ─── Real matching & classification ──────────────────────────────────────────

function toBlankStr(v) {
  return v == null ? "" : String(v).trim();
}

function normNum(v) {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

/**
 * Build a `recordedJsonData` snapshot from a DB record (factory values).
 */
function buildDbSnapshot(dbRecord) {
  return {
    outturnNumber: toBlankStr(dbRecord.outturnNumber),
    growerName: toBlankStr(dbRecord.growersName),
    grower: toBlankStr(dbRecord.growersCode),
    coffeeType: toBlankStr(dbRecord.coffeeType),
    grossWeight: toBlankStr(dbRecord.grossWeight),
    netWeight: toBlankStr(dbRecord.netWeight),
    netWeightWithBags: toBlankStr(dbRecord.netWeightWithBag),
    season: toBlankStr(dbRecord.season),
    deliveryDate: dbRecord.deliveryDate
      ? normalizeParchmentDeliveryDateInput(dbRecord.deliveryDate)
      : "",
    moisture: toBlankStr(dbRecord.moisture),
  };
}

/**
 * Detect all issues for a single CSV row against DB records.
 * Returns an array of { issueType, affectedFields, issueContext, dbRecord }.
 */
function detectAllIssues(row, dbByOutturn, outturnCounts) {
  const issues = [];
  const outturn = toBlankStr(row.outturnNumber);
  const outturnKey = outturn.toLowerCase();

  // 1. Missing outturn — nothing else can be checked
  if (!outturn) {
    issues.push({ issueType: BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER, affectedFields: ["outturnNumber"], issueContext: {}, dbRecord: null });
    return issues;
  }

  // 2. Duplicate in CSV
  if ((outturnCounts.get(outturnKey) || 0) > 1) {
    issues.push({ issueType: BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER, affectedFields: ["outturnNumber"], issueContext: {}, dbRecord: null });
  }

  // 3. Not found in DB — can't compare fields
  const dbRecord = dbByOutturn.get(outturnKey);
  if (!dbRecord) {
    issues.push({ issueType: BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH, affectedFields: ["outturnNumber"], issueContext: { csvOutturnNumber: outturn }, dbRecord: null });
    return issues;
  }

  // 4. Grower identity / credentials
  const csvGrowerName = toBlankStr(row.growerName).toLowerCase();
  const csvGrowerCode = toBlankStr(row.growerCode).toLowerCase();
  const dbGrowerName = toBlankStr(dbRecord.growersName).toLowerCase();
  const dbGrowerCode = toBlankStr(dbRecord.growersCode).toLowerCase();
  const hasCsvGrower = csvGrowerName || csvGrowerCode;
  const hasDbGrower = dbGrowerName || dbGrowerCode;

  if (!hasCsvGrower) {
    issues.push({ issueType: BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS, affectedFields: ["growerName", "growerCode"], issueContext: {}, dbRecord });
  } else if (hasDbGrower) {
    const nameMismatch = csvGrowerName && dbGrowerName && csvGrowerName !== dbGrowerName;
    const codeMismatch = csvGrowerCode && dbGrowerCode && csvGrowerCode !== dbGrowerCode;
    if (nameMismatch || codeMismatch) {
      issues.push({
        issueType: BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH,
        affectedFields: ["growerName", "growerCode"],
        issueContext: {
          mobileGrowerName: toBlankStr(dbRecord.growersName),
          xlsGrowerName: toBlankStr(row.growerName),
        },
        dbRecord,
      });
    }
  }

  // 5. Weights
  const csvGross = normNum(row.grossWeight);
  const dbGross = normNum(dbRecord.grossWeight);
  const csvNet = normNum(row.netWeight) ?? normNum(row.netWeightWithBags);
  const dbNet = normNum(dbRecord.netWeight) ?? normNum(dbRecord.netWeightWithBag);

  const csvWeightBlank = row.netWeight == null || toBlankStr(row.netWeight) === "";
  const csvNetWithBagsBlank = row.netWeightWithBags == null || toBlankStr(row.netWeightWithBags) === "";
  if (csvWeightBlank && csvNetWithBagsBlank) {
    issues.push({ issueType: BULK_ISSUE_TYPE.MISSING_WEIGHT_VALUE, affectedFields: ["netWeight", "netWeightWithBags"], issueContext: {}, dbRecord });
  } else {
    const grossMismatch = csvGross != null && dbGross != null && Math.abs(csvGross - dbGross) > 0.01;
    const netMismatch = csvNet != null && dbNet != null && Math.abs(csvNet - dbNet) > 0.01;
    if (grossMismatch || netMismatch) {
      issues.push({ issueType: BULK_ISSUE_TYPE.WEIGHT_MISMATCH, affectedFields: ["grossWeight", "netWeight"], issueContext: {}, dbRecord });
    }
  }

  // 6. Delivery date — normalize both to YYYY-MM-DD before comparing
  // DB stores dates as "2026-04-22 00:00:00", normalizeParchmentDeliveryDateInput handles that
  const csvDate = normalizeParchmentDeliveryDateInput(row.deliveryDate || "");
  const dbDateRaw = dbRecord.deliveryDate ? String(dbRecord.deliveryDate) : "";
  const dbDate = dbDateRaw ? normalizeParchmentDeliveryDateInput(dbDateRaw) : "";
  if (csvDate && dbDate && csvDate !== dbDate) {
    issues.push({
      issueType: BULK_ISSUE_TYPE.DELIVERY_DATE_MISMATCH,
      affectedFields: ["deliveryDate"],
      issueContext: { csvDeliveryDate: csvDate, factoryDeliveryDate: dbDate },
      dbRecord,
    });
  }

  return issues;
}

/**
 * Issue priority order — the first matching type becomes the primary `issueType`
 * used for filter chips and status pills.
 */
const ISSUE_PRIORITY = [
  BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER,
  BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER,
  BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH,
  BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH,
  BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS,
  BULK_ISSUE_TYPE.MISSING_WEIGHT_VALUE,
  BULK_ISSUE_TYPE.WEIGHT_MISMATCH,
  BULK_ISSUE_TYPE.DELIVERY_DATE_MISMATCH,
  BULK_ISSUE_TYPE.BAGS_QUANTITY_MISMATCH,
];

function pickPrimaryIssueType(issues) {
  for (const priority of ISSUE_PRIORITY) {
    if (issues.some((i) => i.issueType === priority)) return priority;
  }
  return issues[0]?.issueType || null;
}

/**
 * Match CSV rows against DB records and classify each row.
 * @param {Array} parsedRows - rows from parseParchmentDeliveryBulkFile
 * @param {Array} dbRecords  - records from lookupByOutturnNumbers API
 * @returns {object} - result detail object compatible with BulkImportResult.vue
 */
export function buildBulkImportResult(parsedRows, dbRecords) {
  const dbByOutturn = new Map();
  (dbRecords || []).forEach((r) => {
    const key = toBlankStr(r.outturnNumber).toLowerCase();
    if (key) dbByOutturn.set(key, r);
  });

  const outturnCounts = new Map();
  parsedRows.forEach((r) => {
    const key = toBlankStr(r.outturnNumber).toLowerCase();
    if (key) outturnCounts.set(key, (outturnCounts.get(key) || 0) + 1);
  });

  const records = parsedRows.map((r, index) => {
    const issues = detectAllIssues(r, dbByOutturn, outturnCounts);

    // Compute whether outturn+deliveryDate both match the DB record (for override eligibility)
    const outturnKey2 = toBlankStr(r.outturnNumber).toLowerCase();
    const matchedDb = dbByOutturn.get(outturnKey2) || null;
    let deliveryDateMatchesDb = false;
    if (matchedDb) {
      const csvDate2 = normalizeParchmentDeliveryDateInput(r.deliveryDate || "");
      const dbDateRaw2 = matchedDb.deliveryDate ? String(matchedDb.deliveryDate) : "";
      const dbDate2 = dbDateRaw2 ? normalizeParchmentDeliveryDateInput(dbDateRaw2) : "";
      deliveryDateMatchesDb = Boolean(csvDate2 && dbDate2 && csvDate2 === dbDate2);
    }

    const _nwb = Number(toBlankStr(r.netWeightWithBags)) || 0;
    const _bags = Number(toBlankStr(r.bags)) || 0;
    const payload = {
      id: toBlankStr(r.id),
      outturnNumber: toBlankStr(r.outturnNumber),
      grower: toBlankStr(r.growerCode),
      growerName: toBlankStr(r.growerName),
      coffeeType: toBlankStr(r.coffeeType),
      deliveryDate: normalizeParchmentDeliveryDateInput(r.deliveryDate),
      season: toBlankStr(r.season),
      bags: toBlankStr(r.bags),
      grossWeight: toBlankStr(r.grossWeight),
      netWeightWithBags: toBlankStr(r.netWeightWithBags),
      netWeight: toBlankStr(r.netWeight),
      broker: toBlankStr(r.broker),
      moisture: toBlankStr(r.moisture),
      // totalQuantity = netWeightWithBags × bags; unitSize = weight per bag (netWeightWithBags)
      totalQuantity: _nwb && _bags ? _nwb * _bags : null,
      unitSize: _nwb || null,
    };

    if (!issues.length) {
      return {
        id: index + 1,
        status: BULK_RECORD_STATUS.SUCCESS,
        payloadJsonData: payload,
        issueType: null,
        issues: [],
        issueContext: {},
        recordedJsonData: null,
        deliveryDateMatchesDb,
        dbOutturnMatchFound: Boolean(matchedDb),
      };
    }

    const primaryIssueType = pickPrimaryIssueType(issues);
    const primaryIssue = issues.find((i) => i.issueType === primaryIssueType);
    const dbRecord = issues.find((i) => i.dbRecord)?.dbRecord || null;

    return {
      id: index + 1,
      status: BULK_RECORD_STATUS.DATA_DISCREPANCY,
      payloadJsonData: payload,
      issueType: primaryIssueType,
      issues,
      issueContext: primaryIssue?.issueContext || {},
      recordedJsonData: dbRecord ? buildDbSnapshot(dbRecord) : null,
      resolutionApplied: false,
      deliveryDateMatchesDb,
      dbOutturnMatchFound: Boolean(matchedDb),
    };
  });

  const successCount = records.filter((r) => isSuccessRecord(r)).length;
  const discrepancyCount = records.filter((r) => isDataDiscrepancyRecord(r)).length;
  const failedCount = records.filter((r) => isFailedRecord(r)).length;

  return {
    id: Date.now(),
    totalRecordsCount: records.length,
    successRecordsCount: successCount,
    failedRecordsCount: failedCount,
    discrepancyRecordsCount: discrepancyCount,
    importedRecordsCount: successCount + discrepancyCount,
    records,
    templateError: false,
  };
}

const REQUIRED_CANONICAL_KEYS = [
  "id",
  "outturnNumber",
  "growerName",
  "growerCode",
  "coffeeType",
];

function normalizeHeaderKey(value) {
  return String(value ?? "")
    .replace(/^\ufeff/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function buildHeaderToCanonicalMap(rawHeaders) {
  const headerIndexByNorm = new Map(
    (rawHeaders || []).map((h, idx) => [normalizeHeaderKey(h), idx])
  );

  const canonical = {
    id: "id",
    outturnNumber: "outturnnumber",
    deliveryDate: normalizeHeaderKey("Delivery Date"),
    season: "season",
    growerName: normalizeHeaderKey("Grower's Name (Factory Name)"),
    growerCode: normalizeHeaderKey("Grower's Code(Factory Code)"),
    coffeeType: "coffeetype",
    bags: "bags",
    grossWeight: normalizeHeaderKey("Gross Weight"),
    netWeightWithBags: normalizeHeaderKey("Net Weight With Bags"),
    netWeight: normalizeHeaderKey("Net Weight"),
    broker: normalizeHeaderKey("Broker(Agent)"),
    moisture: normalizeHeaderKey("Moisture"),
  };

  const mapping = {};
  Object.entries(canonical).forEach(([key, norm]) => {
    if (headerIndexByNorm.has(norm)) mapping[key] = headerIndexByNorm.get(norm);
  });
  return mapping;
}

function validateTemplateHeaders(rawHeaders) {
  const mapping = buildHeaderToCanonicalMap(rawHeaders);
  const missing = REQUIRED_CANONICAL_KEYS.filter((k) => mapping[k] == null);
  return { valid: missing.length === 0, missing, mapping };
}

function findHeaderRowIndex(aoa) {
  const maxScan = Math.min(20, aoa.length);
  for (let i = 0; i < maxScan; i += 1) {
    const row = aoa[i] || [];
    const rawHeaders = row.map((cell) => String(cell ?? "").trim()).filter(Boolean);
    if (!rawHeaders.length) continue;
    const normalizedSet = new Set(rawHeaders.map(normalizeHeaderKey));

    // Quick heuristic: must include outturnnumber + id.
    if (normalizedSet.has("id") && normalizedSet.has("outturnnumber")) {
      return i;
    }
  }
  return -1;
}

function rowHasAnyData(row, mapping) {
  if (!row || !mapping) return false;
  return REQUIRED_CANONICAL_KEYS.some((k) => {
    const idx = mapping[k];
    if (idx == null) return false;
    const v = row[idx];
    return v != null && String(v).trim() !== "";
  });
}

function buildDummyIssueContextForGrowerMismatch(xlsGrowerName) {
  // These values are used in the `Grower Identity Mismatch` translation template.
  return {
    mobileGrowerName: "James Ochieng",
    xlsGrowerName: xlsGrowerName || "Uploaded Grower",
  };
}

function shouldSimulateOutturnMismatch(outturnNumber, index) {
  const norm = String(outturnNumber || "").toLowerCase();
  return norm.includes("0026") || norm.includes("0012") || index % 3 === 0;
}

function shouldSimulateGrowerIdentityMismatch(outturnNumber, growerCode) {
  const outturnNorm = String(outturnNumber || "").toLowerCase();
  const growerCodeNorm = String(growerCode || "").toLowerCase();
  return (
    outturnNorm.includes("grower-mismatch") ||
    outturnNorm.includes("-gm-") ||
    growerCodeNorm.includes("gm") ||
    growerCodeNorm.includes("grower-mismatch")
  );
}

function buildDummyParchmentDeliveryResult(rows) {
  const records = [];

  const toBlank = (v) => (v == null ? "" : String(v).trim());
  const normOutturn = (v) => toBlank(v).toLowerCase();
  const toNum = (v) => {
    const n = Number(String(v ?? "").replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : null;
  };
  const isBlank = (v) => !toBlank(v);

  const outturnCounts = new Map();
  rows.forEach((r) => {
    const key = normOutturn(r.outturnNumber);
    if (!key) return;
    outturnCounts.set(key, (outturnCounts.get(key) || 0) + 1);
  });

  function createPayload(r) {
    return {
      id: r.id,
      outturnNumber: toBlank(r.outturnNumber),
      grower: toBlank(r.growerCode),
      growerName: toBlank(r.growerName),
      coffeeType: toBlank(r.coffeeType),
      deliveryDate: normalizeParchmentDeliveryDateInput(r.deliveryDate),
      season: toBlank(r.season),
      bags: toBlank(r.bags),
      grossWeight: toBlank(r.grossWeight),
      netWeightWithBags: toBlank(r.netWeightWithBags),
      netWeight: toBlank(r.netWeight),
      broker: toBlank(r.broker),
      moisture: toBlank(r.moisture),
    };
  }

  function createDiscrepancyRecord({ internalId, payload, issueType, issueContext }) {
    const ctx = issueContext || {};
    const recordedJsonData = buildDummyFactorySnapshot(payload, issueType, ctx);
    return {
      id: internalId,
      status: BULK_RECORD_STATUS.DATA_DISCREPANCY,
      payloadJsonData: payload,
      issueType,
      issueContext: ctx,
      recordedJsonData,
      resolutionApplied: false,
    };
  }

  const allIssueTypes = Object.values(BULK_ISSUE_TYPE);

  const SYNTHETIC_ISSUE_SAMPLES = {
    [BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH]: {
      payload: {
        id: "DEMO-OT-MISMATCH",
        outturnNumber: "DEMO-OT-MISMATCH",
        grower: "GR-201",
        growerName: "Demo Grower A",
        coffeeType: "P1",
        deliveryDate: "2025-01-01",
        bags: "10",
        grossWeight: "1000",
        netWeightWithBags: "700",
        netWeight: "700",
        broker: "Direct Sale",
        moisture: "9.0",
      },
      issueContext: {},
    },
    [BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER]: {
      payload: {
        id: "DEMO-OT-DUP",
        outturnNumber: "DEMO-OT-DUPLICATE",
        grower: "GR-202",
        growerName: "Demo Grower B",
        coffeeType: "P1",
        deliveryDate: "2025-01-01",
        bags: "12",
        grossWeight: "1100",
        netWeightWithBags: "800",
        netWeight: "800",
        broker: "Direct Sale",
        moisture: "9.0",
      },
      issueContext: {},
    },
    [BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER]: {
      payload: {
        id: "DEMO-OT-MISSING",
        outturnNumber: "",
        grower: "GR-203",
        growerName: "Demo Grower C",
        coffeeType: "P1",
        deliveryDate: "2025-01-01",
        bags: "13",
        grossWeight: "900",
        netWeightWithBags: "600",
        netWeight: "600",
        broker: "Direct Sale",
        moisture: "9.0",
      },
      issueContext: {},
    },
    [BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH]: {
      payload: {
        id: "DEMO-GROWER-MISMATCH",
        outturnNumber: "DEMO-GM-0012",
        grower: "GR-999",
        growerName: "Jane Wanjiku",
        coffeeType: "P1",
        deliveryDate: "2025-01-01",
        bags: "14",
        grossWeight: "1200",
        netWeightWithBags: "850",
        netWeight: "850",
        broker: "Direct Sale",
        moisture: "9.0",
      },
      issueContext: {
        mobileGrowerName: "James Ochieng",
        xlsGrowerName: "Jane Wanjiku",
      },
    },
    [BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS]: {
      payload: {
        id: "DEMO-NO-GROWER",
        outturnNumber: "DEMO-NOG-0001",
        grower: "",
        growerName: "",
        coffeeType: "P1",
        deliveryDate: "2025-01-01",
        bags: "15",
        grossWeight: "1300",
        netWeightWithBags: "900",
        netWeight: "900",
        broker: "Direct Sale",
        moisture: "9.0",
      },
      issueContext: {},
    },
    [BULK_ISSUE_TYPE.WEIGHT_MISMATCH]: {
      payload: {
        id: "DEMO-WM",
        outturnNumber: "DEMO-WM-0026",
        grower: "GR-204",
        growerName: "Demo Grower D",
        coffeeType: "P1",
        deliveryDate: "2025-01-01",
        bags: "16",
        grossWeight: "1400",
        netWeightWithBags: "1000",
        netWeight: "995",
        broker: "Direct Sale",
        moisture: "9.0",
      },
      issueContext: {},
    },
    [BULK_ISSUE_TYPE.MISSING_WEIGHT_VALUE]: {
      payload: {
        id: "DEMO-MW",
        outturnNumber: "DEMO-MW-0012",
        grower: "GR-205",
        growerName: "Demo Grower E",
        coffeeType: "P1",
        deliveryDate: "2025-01-01",
        bags: "17",
        grossWeight: "1500",
        netWeightWithBags: "",
        netWeight: "",
        broker: "Direct Sale",
        moisture: "9.0",
      },
      issueContext: {},
    },
    [BULK_ISSUE_TYPE.DELIVERY_DATE_MISMATCH]: {
      payload: {
        id: "DEMO-DDM",
        outturnNumber: "DEMO-DDM-0002",
        grower: "GR-206",
        growerName: "Demo Grower F",
        coffeeType: "P1",
        deliveryDate: "2024-12-31",
        bags: "18",
        grossWeight: "1600",
        netWeightWithBags: "1100",
        netWeight: "1100",
        broker: "Direct Sale",
        moisture: "9.0",
      },
      issueContext: {},
    },
    [BULK_ISSUE_TYPE.BAGS_QUANTITY_MISMATCH]: {
      payload: {
        id: "DEMO-BQM",
        outturnNumber: "DEMO-BQM-0003",
        grower: "GR-207",
        growerName: "Demo Grower G",
        coffeeType: "P1",
        deliveryDate: "2025-01-01",
        bags: "0",
        grossWeight: "1700",
        netWeightWithBags: "1200",
        netWeight: "1200",
        broker: "Direct Sale",
        moisture: "9.0",
      },
      issueContext: {},
    },
  };

  function shouldSimulateWeightMismatch(netWeightWithBags, netWeight, index) {
    const a = toNum(netWeightWithBags);
    const b = toNum(netWeight);
    if (a != null && b != null && Math.abs(a - b) > 0.01) return true;
    return index % 7 === 0;
  }

  function shouldSimulateDeliveryDateMismatch(deliveryDate, index) {
    const dd = toBlank(deliveryDate);
    if (!dd) return true;
    return index % 5 === 0;
  }

  function shouldSimulateBagsQuantityMismatch(bags, index) {
    const b = toBlank(bags);
    if (!b) return true;
    return index % 4 === 0;
  }

  rows.forEach((r, index) => {
    const outturnNumber = r.outturnNumber;
    const growerCode = r.growerCode;
    const growerName = r.growerName;

    const isDuplicateOutturn =
      normOutturn(outturnNumber) &&
      (outturnCounts.get(normOutturn(outturnNumber)) || 0) > 1;

    const missingOutturn = isBlank(outturnNumber);
    const missingGrowerCreds = isBlank(growerCode) && isBlank(growerName);

    const missingWeightValue = isBlank(r.netWeightWithBags) && isBlank(r.netWeight);
    const weightMismatch = shouldSimulateWeightMismatch(r.netWeightWithBags, r.netWeight, index);
    const deliveryDateMismatch = shouldSimulateDeliveryDateMismatch(r.deliveryDate, index);
    const bagsQuantityMismatch = shouldSimulateBagsQuantityMismatch(r.bags, index);

    let issueType = null;
    let issueContext = {};

    if (missingOutturn) {
      issueType = BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER;
    } else if (isDuplicateOutturn) {
      issueType = BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER;
    } else if (shouldSimulateOutturnMismatch(outturnNumber, index)) {
      issueType = BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH;
    } else if (shouldSimulateGrowerIdentityMismatch(outturnNumber, growerCode)) {
      issueType = BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH;
      issueContext = buildDummyIssueContextForGrowerMismatch(growerName);
    } else if (missingGrowerCreds) {
      issueType = BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS;
    } else if (missingWeightValue) {
      issueType = BULK_ISSUE_TYPE.MISSING_WEIGHT_VALUE;
    } else if (weightMismatch) {
      issueType = BULK_ISSUE_TYPE.WEIGHT_MISMATCH;
    } else if (deliveryDateMismatch) {
      issueType = BULK_ISSUE_TYPE.DELIVERY_DATE_MISMATCH;
      issueContext = {
        factoryDeliveryDate: "2024-01-04",
      };
    } else if (bagsQuantityMismatch) {
      issueType = BULK_ISSUE_TYPE.BAGS_QUANTITY_MISMATCH;
    }

    const payload = createPayload(r);

    const internalId = index + 1;
    if (issueType) {
      records.push(
        createDiscrepancyRecord({
          internalId,
          payload,
          issueType,
          issueContext,
        })
      );
    } else {
      records.push({
        id: internalId,
        status: BULK_RECORD_STATUS.SUCCESS,
        payloadJsonData: payload,
        issueType: null,
        issueContext: {},
        recordedJsonData: null,
      });
    }
  });

  // Ensure every warning type exists at least once for the demo.
  const presentTypes = new Set(
    records
      .filter((r) => isDataDiscrepancyRecord(r) && r.issueType)
      .map((r) => r.issueType)
  );

  let nextInternalId = records.length + 1;
  allIssueTypes.forEach((issueType) => {
    if (presentTypes.has(issueType)) return;
    const sample = SYNTHETIC_ISSUE_SAMPLES[issueType];
    if (!sample) return;
    records.push(
      createDiscrepancyRecord({
        internalId: nextInternalId,
        payload: sample.payload,
        issueType,
        issueContext: sample.issueContext || {},
      })
    );
    nextInternalId += 1;
  });

  // Demo: include parse/upload failures so the failed-row UI can be verified.
  if (rows.length > 0) {
    const sample = rows[0];
    const failedPayload = createPayload({
      ...sample,
      id: sample.id || "01KK0026-2025-2026",
      outturnNumber: sample.outturnNumber || "01KK0026",
      growerCode: sample.growerCode || "35CE008",
      growerName: sample.growerName || "PILIPILI FOXES",
      coffeeType: sample.coffeeType || "PL",
    });
    records.push({
      id: records.length + 1,
      status: BULK_RECORD_STATUS.FAILED,
      payloadJsonData: failedPayload,
      issueType: null,
      issueContext: {},
      recordedJsonData: null,
      message: "Failed to parse or upload row.",
      errorMessage: "Failed to parse or upload row.",
    });
  }

  const successCount = records.filter((r) => isSuccessRecord(r)).length;
  const discrepancyCount = records.filter((r) => isDataDiscrepancyRecord(r)).length;
  const failedCount = records.filter((r) => isFailedRecord(r)).length;

  return {
    id: Date.now(),
    totalRecordsCount: records.length,
    successRecordsCount: successCount,
    failedRecordsCount: failedCount,
    discrepancyRecordsCount: discrepancyCount,
    importedRecordsCount: successCount + discrepancyCount,
    records,
    templateError: false,
  };
}

function extractRowsFromWorkbook(workbook) {
  const sheetName = workbook.SheetNames?.[0];
  const ws = sheetName ? workbook.Sheets[sheetName] : null;
  if (!ws) {
    return { templateError: true, message: "No worksheet found in the file." };
  }

  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const headerRowIndex = findHeaderRowIndex(aoa);
  if (headerRowIndex < 0) {
    return {
      templateError: true,
      message:
        "Could not find a valid header row. Use the provided template and ensure column headers are present.",
      messageFull:
        "Could not find a valid header row. Use the provided template and ensure column headers are present.",
    };
  }

  const rawHeaders = (aoa[headerRowIndex] || []).map((c) => String(c ?? "").trim());
  const { valid, missing, mapping } = validateTemplateHeaders(rawHeaders);
  if (!valid) {
    return {
      templateError: true,
      message: `Invalid template. Missing or mismatched columns: ${missing.join(", ")}`,
      messageFull: `Invalid template. Missing or mismatched columns: ${missing.join(", ")}`,
    };
  }

  const rows = [];
  for (let i = headerRowIndex + 1; i < aoa.length; i += 1) {
    const row = aoa[i] || [];
    if (!rowHasAnyData(row, mapping)) continue;

    rows.push({
      id: String(row[mapping.id] ?? "").trim(),
      deliveryDate: normalizeParchmentDeliveryDateInput(row[mapping.deliveryDate]),
      outturnNumber: String(row[mapping.outturnNumber] ?? "").trim(),
      season: String(row[mapping.season] ?? "").trim(),
      growerCode: String(row[mapping.growerCode] ?? "").trim(),
      growerName: String(row[mapping.growerName] ?? "").trim(),
      coffeeType: String(row[mapping.coffeeType] ?? "").trim(),
      bags: row[mapping.bags] ?? "",
      grossWeight: row[mapping.grossWeight] ?? "",
      netWeightWithBags: row[mapping.netWeightWithBags] ?? "",
      netWeight: row[mapping.netWeight] ?? "",
      broker: row[mapping.broker] ?? "",
      moisture: row[mapping.moisture] ?? "",
      // Keep extra values for future use/debug.
      rawRow: row,
    });
  }

  if (!rows.length) {
    return {
      templateError: true,
      message: "The uploaded file is empty.",
      messageFull: "The uploaded file is empty.",
    };
  }

  return { templateError: false, rows };
}

export function parseParchmentDeliveryBulkFile(arrayBuffer) {
  // NB: no cellDates — date cells arrive as raw Excel serials and are decoded
  // via XLSX.SSF (calendar components, timezone-immune) in
  // normalizeParchmentDeliveryDateInput. cellDates yields Date objects that
  // shift a day when a UTC-midnight cell is read in a behind-UTC timezone.
  const workbook = XLSX.read(arrayBuffer, { type: "array" });
  return extractRowsFromWorkbook(workbook);
}

/**
 * Parse a File object and return the raw rows (no DB matching / classification).
 * Returns: { ok, templateError, message, messageFull, rows }
 */
export function parseParchmentDeliveryBulkRows(file) {
  const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
  return (ext === ".csv" ? file.text() : file.arrayBuffer())
    .then((fileData) => {
      // No cellDates — dates arrive as raw serials and are decoded via XLSX.SSF
      // (timezone-immune). cellDates would shift a day for UTC-midnight cells
      // read in a behind-UTC timezone.
      const workbook =
        ext === ".csv"
          ? XLSX.read(fileData, { type: "string" })
          : XLSX.read(fileData, { type: "array" });
      const parsed = extractRowsFromWorkbook(workbook);
      if (parsed.templateError) {
        return { ok: false, templateError: true, message: parsed.message, messageFull: parsed.messageFull || parsed.message, rows: [] };
      }
      return { ok: true, templateError: false, rows: parsed.rows || [] };
    })
    .catch((err) => ({
      ok: false,
      templateError: true,
      message: err?.message || "Failed to parse file.",
      messageFull: err?.message || "Failed to parse file.",
      rows: [],
    }));
}

export function processParchmentDeliveryBulkFile(file) {
  const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`;

  return (ext === ".csv" ? file.text() : file.arrayBuffer())
    .then((fileData) => {
      // No cellDates — dates arrive as raw serials and are decoded via XLSX.SSF
      // (timezone-immune). cellDates would shift a day for UTC-midnight cells
      // read in a behind-UTC timezone.
      const workbook =
        ext === ".csv"
          ? XLSX.read(fileData, { type: "string" })
          : XLSX.read(fileData, { type: "array" });

      const parsed = extractRowsFromWorkbook(workbook);

      if (parsed.templateError) {
        return {
          ok: false,
          templateError: true,
          message: parsed.message,
          messageFull: parsed.messageFull || parsed.message,
          data: {
            templateError: true,
            totalRecordsCount: 0,
            failedRecordsCount: 0,
            discrepancyRecordsCount: 0,
            successRecordsCount: 0,
            importedRecordsCount: 0,
            records: [],
            message: parsed.message,
            messageFull: parsed.messageFull || parsed.message,
          },
        };
      }

      const resultData = buildDummyParchmentDeliveryResult(parsed.rows);
      return {
        ok: resultData.totalRecordsCount > 0,
        templateError: false,
        data: resultData,
      };
    })
    .catch((err) => {
      // Keep consistent return shape with UI error handling.
      return {
        ok: false,
        templateError: true,
        message: err?.message || "Failed to parse file.",
        messageFull: err?.message || "Failed to parse file.",
        data: {
          templateError: true,
          totalRecordsCount: 0,
          failedRecordsCount: 0,
          discrepancyRecordsCount: 0,
          successRecordsCount: 0,
          importedRecordsCount: 0,
          records: [],
          message: err?.message || "Failed to parse file.",
          messageFull: err?.message || "Failed to parse file.",
        },
      };
    });
}


